import { isEditorContentEmpty } from 'components/CommonComponents/RichTextEditor/utils'
import keys from 'constants/keys'
import { Content, Subtask, TaskFormData } from 'components/TasksV2/TaskCreation/model/types'
import { IContentFormData, INewCategory, INewSubtask } from '../types'

export function categoryDraftToTaskContent(
  draft: IContentFormData,
  preserveDescriptionPositions = false
): TaskFormData['SUBTASK_TASK_CREATION'] {
  const descriptions =
    draft.description_blocks ||
    [
      {
        type: keys.SUBTASK_TYPES.DESCRIPTION,
        text: null,
        rich_text: draft.description_rich_text || null,
        images: draft.description_images || [],
        videos: draft.description_videos || [],
        files: draft.description_files || [],
        index_ref: 0,
      },
    ].filter(
      (content) =>
        !isEditorContentEmpty(content.rich_text) ||
        content.images.length ||
        content.videos.length ||
        content.files.length
    )
  const subtasks = draft.categories.flatMap((category) =>
    category.subtasks.map((subtask) => ({
      ...subtask.task_subtask,
      id: subtask.id,
      title: subtask.title || '',
      type: subtask.type,
      is_required: subtask.is_required ?? true,
      category: category.name,
      content: {
        ...subtask.task_subtask?.content,
        rich_text: subtask.description || null,
        images: subtask.images || [],
        videos: subtask.videos || [],
        files: subtask.files || [],
      },
      ...(subtask.is_ai_enhanced && subtask.type !== keys.SUBTASK_TYPES.VIDEO_SUBTASKS
        ? {
            is_ai_enhanced: true,
            ai_prompt: subtask.ai_prompt || '',
            prompt_id: subtask.task_subtask?.prompt_id || keys.AI_IMAGE_SUBTASK.CONST_PROMPT_ID,
            reference_images: subtask.reference_images || subtask.images || [],
          }
        : {}),
    }))
  )

  const ordered: (typeof subtasks[number] | Content)[] = [...subtasks]
  if (preserveDescriptionPositions) {
    // Editing cannot reorder existing subtasks or move their intervening information blocks.
    descriptions
      .slice()
      .sort((a, b) => a.index_ref - b.index_ref)
      .forEach((block) => {
        ordered.splice(Math.min(block.index_ref, ordered.length), 0, block)
      })
  } else {
    ordered.unshift(...descriptions)
  }

  return {
    title: draft.title,
    sub_tasks: ordered.map((subtask, index) => ({
      ...subtask,
      order_index: index,
    })) as Subtask[],
  }
}

/** Keep empty category metadata and stable IDs while storing the shared editor's live values. */
export function taskContentToCategoryDraft(
  content: { title?: string; sub_tasks?: (Subtask | Content)[] },
  categories: Pick<INewCategory, 'id' | 'name'>[],
  fieldIds: string[] = []
): IContentFormData {
  const subtasks = content.sub_tasks || []
  const descriptions = subtasks.filter((subtask): subtask is Content => 'index_ref' in subtask)
  const first = descriptions[0]

  return {
    title: content.title || '',
    categories: categories.map((category) => ({
      ...category,
      subtasks: subtasks.flatMap((subtask, index): INewSubtask[] => {
        if ('index_ref' in subtask || (subtask.category || '') !== category.name) return []
        return [
          {
            task_subtask: subtask,
            id: fieldIds[index] || subtask.id || `${category.id}-${index}`,
            title: subtask.title,
            type: subtask.type,
            is_required: subtask.is_required ?? true,
            description: subtask.content?.rich_text || null,
            images: subtask.content?.images || [],
            videos: subtask.content?.videos || [],
            files: subtask.content?.files || [],
            ...(subtask.is_ai_enhanced
              ? {
                  is_ai_enhanced: true,
                  ai_prompt: subtask.ai_prompt || '',
                  reference_images: subtask.reference_images || [],
                }
              : {}),
          },
        ]
      }),
    })),
    description_blocks: descriptions,
    description_rich_text: first?.rich_text || null,
    description_images: first?.images || [],
    description_videos: first?.videos || [],
    description_files: first?.files || [],
  }
}

export function getEmptyCategories(content: IContentFormData) {
  // The unnamed group only retains existing uncategorized subtasks; it has no category controls.
  return content.categories.filter((category) => !!category.name && !category.subtasks.length)
}
